import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { useI18n } from "@/i18n";
import { toast } from "sonner";
import { Check, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CUSTOM_BUDGET_VALUE = "__custom_budget__";

const initialState = {
  name: "",
  business_name: "",
  email: "",
  phone: "",
  social_links: "",
  service_type: "",
  budget_range: "",
  custom_budget_usd: "",
  project_description: "",
  preferred_start_date: "",
  agreed_minimum: true,
};

export default function QuoteForm() {
  const { t } = useI18n();
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formError, setFormError] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const customAmount = Number(form.custom_budget_usd);
    if (
      form.budget_range === CUSTOM_BUDGET_VALUE
      && (!Number.isFinite(customAmount) || customAmount <= 0)
    ) {
      setFormError(t.quote.custom_budget_error);
      toast.error(t.quote.custom_budget_error);
      return;
    }

    setSubmitting(true);
    setFormError("");
    try {
      const { custom_budget_usd: customBudgetUsd, ...payload } = form;
      if (payload.budget_range === CUSTOM_BUDGET_VALUE) {
        payload.budget_range = `USD $${Number(customBudgetUsd).toLocaleString("en-US", {
          maximumFractionDigits: 2,
        })}`;
      }
      await axios.post(`${API}/quote-requests`, payload);
      setSubmitted(true);
      setForm(initialState);
      toast.success(t.quote.success_title);
    } catch (err) {
      const status = err.response?.status;
      const message = status === 422
        ? t.quote.validation_error
        : err.response
          ? t.quote.server_error
          : t.quote.connection_error;
      setFormError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="quote"
      data-testid="quote-section"
      className="relative bg-black py-28 md:py-40 overflow-hidden"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-black via-zinc-950 to-black" />
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-white/[0.03] blur-3xl" />

      <div className="relative container mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-[1fr_1.4fr] gap-16 lg:gap-24">
          {/* Left: copy */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8 }}
              className="flex items-center gap-3 mb-10"
            >
              <span className="w-10 h-px bg-lux" />
              <span className="text-[11px] uppercase tracking-[0.3em] text-lux">
                {t.quote.label}
              </span>
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9 }}
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter mb-6"
            >
              {t.quote.title}
            </motion.h2>
            <p className="text-white/60 max-w-md text-[15px] leading-relaxed">
              {t.quote.subtitle}
            </p>

            <div className="mt-16 space-y-6 text-sm">
              <div className="flex items-center gap-4 text-white/60">
                <span className="w-1 h-1 rounded-full bg-lux" />
                josuesevilla26sep@gmail.com
              </div>
              <div className="flex items-center gap-4 text-white/60">
                <span className="w-1 h-1 rounded-full bg-lux" />
                +1 (407) 541-9482
              </div>
              <div className="flex items-center gap-4 text-white/60">
                <span className="w-1 h-1 rounded-full bg-lux" />
                Miami · Tulsa · Dallas
              </div>
            </div>
          </div>

          {/* Right: form */}
          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.1 }}
            data-testid="quote-form"
            className="luxury-glass rounded-2xl p-8 md:p-12"
          >
            {submitted ? (
              <div data-testid="quote-success" className="py-16 text-center">
                <div className="w-16 h-16 rounded-full bg-white text-black mx-auto flex items-center justify-center mb-6">
                  <Check size={26} />
                </div>
                <h3 className="font-display text-3xl mb-3">{t.quote.success_title}</h3>
                <p className="text-white/60 max-w-md mx-auto">{t.quote.success_desc}</p>
                <button
                  type="button"
                  onClick={() => setSubmitted(false)}
                  className="mt-8 text-xs uppercase tracking-[0.25em] text-white/60 hover:text-white"
                >
                  ↺
                </button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-x-8 gap-y-5">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.3em] text-white/40">
                    {t.quote.fields.name}
                  </label>
                  <input
                    data-testid="qf-name"
                    type="text"
                    required
                    maxLength={120}
                    value={form.name}
                    onChange={(e) => set("name", e.target.value)}
                    className="luxury-input"
                    placeholder="Jane Doe"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.3em] text-white/40">
                    {t.quote.fields.business}
                  </label>
                  <input
                    data-testid="qf-business"
                    type="text"
                    maxLength={200}
                    value={form.business_name}
                    onChange={(e) => set("business_name", e.target.value)}
                    className="luxury-input"
                    placeholder="Acme Co."
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.3em] text-white/40">
                    {t.quote.fields.email}
                  </label>
                  <input
                    data-testid="qf-email"
                    type="email"
                    required
                    maxLength={254}
                    value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                    className="luxury-input"
                    placeholder="you@brand.com"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.3em] text-white/40">
                    {t.quote.fields.phone}
                  </label>
                  <input
                    data-testid="qf-phone"
                    type="tel"
                    required
                    minLength={4}
                    maxLength={40}
                    value={form.phone}
                    onChange={(e) => set("phone", e.target.value)}
                    className="luxury-input"
                    placeholder="+1 555 0100"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-[10px] uppercase tracking-[0.3em] text-white/40">
                    {t.quote.fields.social}
                  </label>
                  <input
                    data-testid="qf-social"
                    type="text"
                    maxLength={500}
                    value={form.social_links}
                    onChange={(e) => set("social_links", e.target.value)}
                    className="luxury-input"
                    placeholder="@yourbrand · instagram.com/… · tiktok.com/…"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.3em] text-white/40">
                    {t.quote.fields.service}
                  </label>
                  <select
                    data-testid="qf-service"
                    required
                    value={form.service_type}
                    onChange={(e) => set("service_type", e.target.value)}
                    className="luxury-input appearance-none cursor-pointer"
                  >
                    <option value="" className="bg-black">
                      {t.quote.fields.service_placeholder}
                    </option>
                    {t.quote.service_options.map((o) => (
                      <option key={o} value={o} className="bg-black">
                        {o}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.3em] text-white/40">
                    {t.quote.fields.budget}
                  </label>
                  <select
                    data-testid="qf-budget"
                    required
                    value={form.budget_range}
                    onChange={(e) => {
                      const value = e.target.value;
                      setForm((current) => ({
                        ...current,
                        budget_range: value,
                        custom_budget_usd: value === CUSTOM_BUDGET_VALUE
                          ? current.custom_budget_usd
                          : "",
                      }));
                    }}
                    className="luxury-input appearance-none cursor-pointer"
                  >
                    <option value="" className="bg-black">
                      {t.quote.fields.budget_placeholder}
                    </option>
                    {t.quote.budget_options.map((o) => (
                      <option key={o} value={o} className="bg-black">
                        {o}
                      </option>
                    ))}
                    <option value={CUSTOM_BUDGET_VALUE} className="bg-black">
                      {t.quote.custom_budget_option}
                    </option>
                  </select>
                </div>
                {form.budget_range === CUSTOM_BUDGET_VALUE && (
                  <div className="md:col-span-2" data-testid="custom-budget-field">
                    <label
                      className="text-[10px] uppercase tracking-[0.3em] text-white/40"
                      htmlFor="custom-budget-usd"
                      data-testid="custom-budget-label"
                    >
                      {t.quote.fields.custom_budget}
                    </label>
                    <div className="relative">
                      <span
                        className="pointer-events-none absolute left-0 top-1/2 -translate-y-1/2 text-lux"
                        data-testid="custom-budget-currency"
                      >
                        $
                      </span>
                      <input
                        id="custom-budget-usd"
                        data-testid="qf-custom-budget"
                        type="number"
                        min="1"
                        step="1"
                        required
                        inputMode="numeric"
                        value={form.custom_budget_usd}
                        onChange={(e) => set("custom_budget_usd", e.target.value)}
                        className="luxury-input pl-5"
                        placeholder={t.quote.custom_budget_placeholder}
                      />
                    </div>
                  </div>
                )}
                <div className="md:col-span-2">
                  <label className="text-[10px] uppercase tracking-[0.3em] text-white/40">
                    {t.quote.fields.description}
                  </label>
                  <textarea
                    data-testid="qf-description"
                    required
                    minLength={10}
                    maxLength={4000}
                    rows={4}
                    value={form.project_description}
                    onChange={(e) => set("project_description", e.target.value)}
                    className="luxury-input"
                    placeholder={t.quote.fields.description_placeholder}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-[10px] uppercase tracking-[0.3em] text-white/40">
                    {t.quote.fields.start}
                  </label>
                  <input
                    data-testid="qf-start"
                    type="date"
                    value={form.preferred_start_date}
                    onChange={(e) => set("preferred_start_date", e.target.value)}
                    className="luxury-input"
                  />
                </div>

                <div className="md:col-span-2 flex items-start gap-3 mt-2">
                  <span className="mt-0.5 w-5 h-5 rounded-full bg-lux/20 border border-lux/40 flex items-center justify-center shrink-0">
                    <span className="w-2 h-2 rounded-full bg-lux" />
                  </span>
                  <span className="text-sm text-white/65">
                    <span className="text-lux font-medium">{t.quote.travel_highlight}</span>{" "}
                    {t.quote.travel_desc}
                  </span>
                </div>

                {formError && (
                  <div
                    role="alert"
                    className="md:col-span-2 border-l-2 border-red-400/60 bg-red-400/[0.06] px-4 py-3 text-sm leading-relaxed text-red-100"
                    data-testid="quote-form-error"
                  >
                    {formError}{" "}
                    <a
                      href="mailto:josuesevilla26sep@gmail.com"
                      className="text-lux underline underline-offset-4 hover:text-lux-soft"
                      data-testid="quote-error-email-link"
                    >
                      {t.quote.email_directly}
                    </a>
                  </div>
                )}

                <button
                  data-testid="quote-submit"
                  type="submit"
                  disabled={submitting}
                  className="md:col-span-2 mt-4 inline-flex items-center justify-center gap-3 bg-white text-black rounded-full px-8 py-4 text-sm uppercase tracking-[0.22em] font-medium hover:bg-zinc-200 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? t.quote.submitting : t.quote.submit}
                  {!submitting && <Send size={14} />}
                </button>
              </div>
            )}
          </motion.form>
        </div>
      </div>
    </section>
  );
}
